/**
 * Blob download helpers — trigger a browser "Save as…" from an Axios blob
 * response while honouring the server's Content-Disposition filename.
 *
 * Usage:
 *   import { downloadBlobResponse, parseFilename } from '../utils/download';
 *   const res = await api.get(url, { responseType: 'blob' });
 *   downloadBlobResponse(res, 'fallback.pdf');
 */

/**
 * Extract a filename from a Content-Disposition header.
 * Handles both `filename="x.pdf"` and RFC 5987 `filename*=UTF-8''x.pdf`.
 * @param {string} [disposition]
 * @returns {string|null}
 */
export function parseFilename(disposition) {
  if (!disposition || typeof disposition !== 'string') return null;
  // RFC 5987 — filename*=UTF-8''percent%20encoded.pdf
  const star = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  if (plain && plain[1]) return plain[1].trim();
  return null;
}

/**
 * Trigger a browser download for a Blob.
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Release the object URL on the next tick so the click has fired.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Download an Axios response whose `responseType` was 'blob'.
 * Prefers the Content-Disposition filename, falling back to `fallbackName`.
 * @param {{ data: Blob, headers?: Record<string, string> }} response
 * @param {string} fallbackName
 */
export function downloadBlobResponse(response, fallbackName) {
  const disposition =
    response?.headers?.['content-disposition'] ||
    response?.headers?.['Content-Disposition'];
  const name = parseFilename(disposition) || fallbackName;
  downloadBlob(response.data, name);
}

/**
 * Some servers return an error body (JSON) even when `responseType: 'blob'`
 * was requested — Axios then resolves the *error* with a Blob payload. This
 * reads that blob back into a JS object so callers can surface `detail`.
 * @param {Blob} blob
 * @returns {Promise<any|null>}
 */
export async function readBlobError(blob) {
  if (!blob || typeof blob.text !== 'function') return null;
  try {
    const text = await blob.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
}
